import React, { useMemo, useState } from 'react';
import { isBrickFactoryUser } from '../utils/businessType';
import { usePrinting } from '../PrintingContext';
import { printServiceLabel } from '../utils/printServiceLabels';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import './ReportPages.css';
import ReportFooter from '../components/ReportFooter';


const ReportPrintingPage = ({ onNavigate, user }) => {
  const factoryMode = isBrickFactoryUser(user);
  const { printJobs, stats, RATE_BW, RATE_COLOR, RATE_COPY, RATE_SCAN, RATE_CUSTOMER_REAM } = usePrinting();
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const filteredJobs = useMemo(
    () =>
      (printJobs || []).filter(
        (j) => (statusFilter === 'all' || j.status === statusFilter) && (typeFilter === 'all' || j.printType === typeFilter)
      ),
    [printJobs, statusFilter, typeFilter]
  );

  const jobTotals = useMemo(
    () =>
      filteredJobs.reduce(
        (acc, j) => ({
          pages: acc.pages + (Number(j.pages) || 0),
          total: acc.total + (Number(j.totalAmount) || 0),
          paid: acc.paid + (Number(j.paidAmount) || 0),
          balance: acc.balance + (Number(j.balance) || 0),
        }),
        { pages: 0, total: 0, paid: 0, balance: 0 }
      ),
    [filteredJobs]
  );

  const exportRows = useMemo(() => {
    const body = filteredJobs.map((j) => [
      j.id,
      j.createdAt,
      j.pages,
      printServiceLabel(j.printType),
      j.rate,
      j.totalAmount,
      j.paidAmount,
      j.balance,
      j.status,
      j.createdBy,
    ]);
    if (filteredJobs.length > 0) {
      body.push(['TOTAL', '—', jobTotals.pages, '—', '—', jobTotals.total, jobTotals.paid, jobTotals.balance, '—', '—']);
    }
    return body;
  }, [filteredJobs, jobTotals]);

  if (factoryMode) {
    return (
      <div className="report-shell">
        <div className="report-back">
          <button type="button" onClick={() => onNavigate('reports')}>
            ← All reports
          </button>
        </div>
        <div className="report-title-center">
          <h1>Printing &amp; copy report</h1>
        </div>
        <p className="muted">Printing reports are not available for brick factory users.</p>
      </div>
    );
  }

  return (
    <div className="report-shell">
      <div className="report-back">
        <button type="button" onClick={() => onNavigate('reports')}>
          ← All reports
        </button>
      </div>
      <div className="report-title-center">
        <h1>Printing &amp; copy report</h1>
      </div>
      <p className="muted">
        Owner rates (TZS / page): B/W {RATE_BW.toLocaleString()}, Color {RATE_COLOR.toLocaleString()}, Copy {RATE_COPY.toLocaleString()},
        Scan {RATE_SCAN.toLocaleString()}, Customer own ream {RATE_CUSTOMER_REAM.toLocaleString()}.
      </p>
      <div className="report-toolbar">
        <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <div className="field">
          <label>Service</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="bw">B/W print</option>
            <option value="color">Color print</option>
            <option value="copy">Photocopy</option>
            <option value="scan">Scan</option>
            <option value="customer-ream">Customer with own ream</option>
          </select>
        </div>
        <div className="report-actions">
          <button
            type="button"
            onClick={async () => {
              try {
                await exportTableToPdf(
                  'Printing and copy report',
                  ['ID', 'Date', 'Pages', 'Service', 'Rate', 'Total', 'Paid', 'Balance', 'Status', 'Created by'],
                  exportRows,
                  {
                    subtitle: 'Printing operations and collections',
                    criteria: [`Status: ${statusFilter}`, `Service: ${typeFilter}`],
                    summary: [
                      { label: 'Jobs', value: filteredJobs.length.toLocaleString() },
                      { label: 'Pages', value: jobTotals.pages.toLocaleString() },
                      { label: 'Billed', value: `TZS ${Math.round(jobTotals.total).toLocaleString()}` },
                      { label: 'Collected', value: `TZS ${Math.round(jobTotals.paid).toLocaleString()}` },
                    ],
                  }
                );
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Export PDF
          </button>
          <button
            type="button"
            onClick={() =>
              downloadCsv('printing-report.csv', [
                ['ID', 'Date', 'Pages', 'Service', 'Rate', 'Total', 'Paid', 'Balance', 'Status', 'Created by'],
                ...exportRows,
              ])
            }
          >
            Export Excel
          </button>
        </div>
      </div>
      <div className="report-grid-cards">
        <div className="report-stat">
          <span>Total jobs</span>
          <strong>{stats.totalJobs}</strong>
        </div>
        <div className="report-stat">
          <span>Total collected</span>
          <strong>TZS {stats.totalRevenue.toLocaleString()}</strong>
        </div>
        <div className="report-stat">
          <span>Pending balance</span>
          <strong>TZS {stats.pendingBalance.toLocaleString()}</strong>
        </div>
      </div>
      <div className="report-table-wrap">
        <h3>Jobs ({filteredJobs.length})</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Pages</th>
              <th>Service</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', color: '#94a3b8' }}>
                  No jobs match this filter.
                </td>
              </tr>
            ) : (
              filteredJobs.map((j) => (
                <tr key={j.id}>
                  <td>{j.id}</td>
                  <td>{j.createdAt}</td>
                  <td>{j.pages}</td>
                  <td>{printServiceLabel(j.printType)}</td>
                  <td>{Number(j.totalAmount).toLocaleString()}</td>
                  <td>{Number(j.paidAmount).toLocaleString()}</td>
                  <td>{Number(j.balance).toLocaleString()}</td>
                  <td>{j.status}</td>
                  <td>{j.createdBy}</td>
                </tr>
              ))
            )}
          </tbody>
          {filteredJobs.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={2}>
                  <strong>Totals</strong>
                  <span className="report-table-totals-muted"> (filtered)</span>
                </td>
                <td>{jobTotals.pages.toLocaleString()}</td>
                <td />
                <td>{Math.round(jobTotals.total).toLocaleString()}</td>
                <td>{Math.round(jobTotals.paid).toLocaleString()}</td>
                <td>{Math.round(jobTotals.balance).toLocaleString()}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <ReportFooter />
    </div>
  );
};

export default ReportPrintingPage;
